import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import axiosInstance from '../../../services/AxiosInstance';
import PageTitle from '../../../jsx/layouts/PageTitle';
import notify from '../../../utils/notification';

const TYPES = [
  { value: '', label: 'All places' },
  { value: 'venue', label: 'Venue gate' },
  { value: 'zone', label: 'Zone' },
  { value: 'dining', label: 'Dining' },
  { value: 'transport', label: 'Transport' },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.value, t.label]));
const EMPTY = { type: '', search: '', from: '', to: '' };

// How many times each person went IN and OUT at each place, counted from the scanner's IN/OUT scans.
const EntryExitReport = () => {
  const [filters, setFilters] = useState(EMPTY);
  const [list, setList] = useState({ rows: [], page: 1, totalPages: 1, total: 0, loading: true });
  const [detail, setDetail] = useState(null); // { row, events, loading }

  const load = useCallback(async (page, f) => {
    setList((l) => ({ ...l, loading: true }));
    try {
      const { data } = await axiosInstance.get('/security/access-report', {
        params: { page, limit: 20, checkpoint_type: f.type || undefined, search: f.search || undefined, from: f.from || undefined, to: f.to || undefined },
      });
      setList({ rows: data.data, page, totalPages: data.totalPages, total: data.totalItems, loading: false });
    } catch (e) {
      setList((l) => ({ ...l, loading: false }));
      notify('error', e.response?.data?.message || e.message);
    }
  }, []);

  // reload when a filter changes (typing in the search box waits a moment)
  useEffect(() => {
    const t = setTimeout(() => load(1, filters), filters.search ? 400 : 0);
    return () => clearTimeout(t);
  }, [filters, load]);

  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  const open = async (row) => {
    setDetail({ row, events: [], loading: true });
    try {
      const { data } = await axiosInstance.get('/security/scan-logs', {
        params: { registration_data_id: row.registration_data_id, checkpoint_type: row.place_type, checkpoint_id: row.place_id, from: filters.from || undefined, to: filters.to || undefined, limit: 100 },
      });
      setDetail({ row, events: data.data, loading: false });
    } catch (e) {
      setDetail(null);
      notify('error', e.response?.data?.message || e.message);
    }
  };

  return (
    <>
      <PageTitle motherMenu="Scan Logs" activeMenu="Entry / Exit Report" />

      <div className="card">
        <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
          <h4 className="card-title mb-0">Entries and exits by person and place ({list.total})</h4>
          <button className="btn btn-outline-primary btn-sm" onClick={() => load(list.page, filters)}>
            Refresh
          </button>
        </div>

        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1">Person</label>
              <input className="form-control" placeholder="Name, email or accreditation #" value={filters.search} onChange={(e) => set({ search: e.target.value })} />
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted mb-1">Place type</label>
              <select className="form-select" value={filters.type} onChange={(e) => set({ type: e.target.value })}>
                {TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small text-muted mb-1">From</label>
              <input type="date" className="form-control" value={filters.from} max={filters.to || undefined} onChange={(e) => set({ from: e.target.value })} />
            </div>
            <div className="col-md-2">
              <label className="form-label small text-muted mb-1">To</label>
              <input type="date" className="form-control" value={filters.to} min={filters.from || undefined} onChange={(e) => set({ to: e.target.value })} />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button className="btn btn-outline-secondary w-100" onClick={() => setFilters(EMPTY)}>
                Reset
              </button>
            </div>
          </div>

          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Person</th><th>User type</th><th>Place</th>
                  <th className="text-center">Entries</th><th className="text-center">Exits</th><th className="text-center">Denied</th>
                  <th>Inside now</th><th>Last scan</th><th className="text-center">Action</th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((r) => (
                  <tr key={`${r.registration_data_id}-${r.place_type}-${r.place_id}`}>
                    <td>
                      {r.person || 'Unknown'}
                      <div className="small text-muted">#{r.registration_data_id}{r.email ? ` · ${r.email}` : ''}</div>
                    </td>
                    <td>{r.user_type || '—'}</td>
                    <td>
                      {r.place}
                      <div className="small text-muted">{TYPE_LABEL[r.place_type]}</div>
                    </td>
                    <td className="text-center fw-bold">{r.entries}</td>
                    <td className="text-center fw-bold">{r.exits}</td>
                    <td className="text-center">{r.denied ? <span className="badge bg-danger">{r.denied}</span> : 0}</td>
                    <td>{r.inside ? <span className="badge bg-success">Inside</span> : <span className="badge bg-secondary">Outside</span>}</td>
                    <td className="text-nowrap">{new Date(r.last_at).toLocaleString()}</td>
                    <td className="text-center">
                      <button className="btn btn-outline-primary btn-sm" onClick={() => open(r)}>View scans</button>
                    </td>
                  </tr>
                ))}
                {!list.loading && !list.rows.length && (
                  <tr><td colSpan={9} className="text-center text-muted">No entries or exits recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {list.totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center">
              <button className="btn btn-outline-secondary btn-sm" disabled={list.page <= 1 || list.loading} onClick={() => load(list.page - 1, filters)}>Previous</button>
              <span className="small text-muted">Page {list.page} of {list.totalPages}</span>
              <button className="btn btn-outline-secondary btn-sm" disabled={list.page >= list.totalPages || list.loading} onClick={() => load(list.page + 1, filters)}>Next</button>
            </div>
          )}
        </div>
      </div>

      <Modal show={!!detail} onHide={() => setDetail(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">
            {detail?.row.person || 'Person'} at {detail?.row.place}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detail?.loading ? (
            <Spinner size="sm" animation="border" />
          ) : (
            <table className="table table-sm align-middle mb-0">
              <thead>
                <tr><th>Time</th><th>Direction</th><th>Scanned by</th><th>Location</th><th>Result</th></tr>
              </thead>
              <tbody>
                {detail?.events.map((e) => (
                  <tr key={e.id} style={e.voided ? { opacity: 0.5, textDecoration: 'line-through' } : undefined}>
                    <td className="text-nowrap">{new Date(e.scanned_at).toLocaleString()}</td>
                    <td>{e.direction ? <span className={`badge ${e.direction === 'in' ? 'bg-success' : 'bg-primary'}`}>{e.direction.toUpperCase()}</span> : '—'}</td>
                    <td>{e.guard}</td>
                    <td>{e.geo_status ? <span className={`badge ${e.geo_status === 'inside' ? 'bg-success' : e.geo_status === 'outside' ? 'bg-danger' : 'bg-secondary'}`}>{e.geo_status === 'inside' ? 'Inside' : e.geo_status === 'outside' ? `${e.geo_distance} m away` : 'Unknown'}</span> : '—'}</td>
                    <td>{e.allowed ? 'Allowed' : e.reason || e.status}{e.voided ? ' (undone)' : ''}</td>
                  </tr>
                ))}
                {!detail?.events.length && <tr><td colSpan={5} className="text-center text-muted">No scans.</td></tr>}
              </tbody>
            </table>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default EntryExitReport;
